import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { workspaceTeamAPI } from '../services/api';
import { confirm } from '../components/ConfirmDialog';
import DjamannLoader from '../components/DjamannLoader';

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadInvitation = useCallback(async () => {
    try {
      setLoading(true);
      const res = await workspaceTeamAPI.getInvitationInfo(token);
      setInvitation(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Invitation invalide ou expirée');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadInvitation();
    }
  }, [token, loadInvitation]);

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      const res = await workspaceTeamAPI.acceptByToken(token);
      setResult({ success: true, message: res.data.message || 'Invitation acceptée !' });
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.error || 'Erreur lors de l\'acceptation' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    const ok = await confirm({ message: 'Voulez-vous vraiment refuser cette invitation ?' });
    if (!ok) return;
    setActionLoading(true);
    try {
      await workspaceTeamAPI.declineInvitation({ token });
      setResult({ success: false, message: 'Invitation refusée.' });
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.error || 'Erreur' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <DjamannLoader variant="fullscreen" />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <span className="text-6xl mb-4 block">❌</span>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invitation invalide</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <span className="text-6xl mb-4 block">{result.success ? '🎉' : '👋'}</span>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {result.success ? 'Bienvenue dans l\'équipe !' : 'Invitation traitée'}
          </h2>
          <p className="text-gray-500 mb-6">{result.message}</p>
          <div className="space-y-3">
            {result.success && (
              <button
                onClick={() => navigate('/workspace/team-invitations')}
                className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium"
              >
                👥 Voir mes workspaces
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative">
            <span className="text-6xl mb-4 block">📨</span>
            <h1 className="text-2xl font-bold mb-2">Invitation d'équipe</h1>
            <p className="text-indigo-100">Vous êtes invité(e) à gérer un workspace</p>
          </div>
        </div>

        {/* Contenu */}
        <div className="p-6 md:p-8">
          {/* Propriétaire */}
          <div className="bg-indigo-50 rounded-2xl p-4 mb-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {invitation?.owner_photo ? (
                <img src={invitation.owner_photo} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                invitation?.owner_name?.charAt(0)?.toUpperCase() || '?'
              )}
            </div>
            <div>
              <div className="text-sm text-indigo-600 font-medium">Invité par</div>
              <div className="text-lg font-bold text-gray-900">{invitation?.owner_name}</div>
            </div>
          </div>

          {/* Rôle proposé */}
          <div className="bg-purple-50 rounded-2xl p-4 mb-6">
            <div className="text-sm text-purple-600 font-medium mb-1">Rôle proposé</div>
            <div className="text-xl font-bold text-gray-900">{invitation?.role_label}</div>
            <p className="text-sm text-gray-500 mt-1">
              Vous garderez votre propre workspace intact. Cet accès est en plus.
            </p>
          </div>

          {/* Info utilisateur connecté */}
          {user && (
            <div className="bg-gray-50 rounded-xl p-3 mb-6 flex items-center gap-3">
              <span className="text-xl">👤</span>
              <div>
                <div className="text-sm font-medium text-gray-700">Connecté en tant que</div>
                <div className="text-xs text-gray-500">{user.email}</div>
              </div>
              {invitation?.member_email && user.email !== invitation.member_email && (
                <div className="ml-auto">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                    ⚠️ Email différent
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              disabled={actionLoading}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium disabled:opacity-50"
            >
              Refuser
            </button>
            <button
              onClick={handleAccept}
              disabled={actionLoading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition font-medium shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {actionLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              ) : (
                <>✅ Accepter</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
